import { memo, useEffect, useRef, useState } from 'react';
import { Button, Col, Form, InputGroup, Spinner } from 'react-bootstrap';
import { Formik, Form as FormikForm, FormikProps } from 'formik';
import * as Yup from 'yup';

import useApi from 'shared/hooks/useApi';
import CustomerOnboardingHttp from 'pages/customer-onboarding/services/customer.service';

import Verified from 'assets/images/verified.gif';
import infoIco from 'assets/images/i-circle.svg';
import { userStore } from 'store/UserStore';
import DashboardHttp from '../services/profile.service';
import { toast } from 'react-toastify';
import { netcoreTrack, netcoreDate } from 'utils/netcoreTracking';

type Props = {};

const CustomerPan = ({}: Props) => {
  // Refs
  const formikRef = useRef<FormikProps<any>>();

  // States
  const [isPanChanged, setIsPanChanged] = useState<boolean>(false);
  const [panValue, setPanValue] = useState<string>('');
  const [pan, setPan] = useState<string>('');
  const [setCustomer, customer, customerId, user] = userStore((state: any) => [
    state.setCustomer,
    state.customer,
    state.customerId,
    state.user,
  ]);

  // Custom Hooks
  let panVerifyApi = useApi(CustomerOnboardingHttp.verifyPanNumber);
  let updateCustomerApi = useApi(CustomerOnboardingHttp.updateCustomer);
  let getCustomerProfileApi = useApi(DashboardHttp.getProfile);

  // Effects
  useEffect(() => {
    if (customer?.kyc?.pan?.value) {
      setPan(customer?.kyc?.pan?.value);
    }
  }, []);

  useEffect(() => {
    if (customer?.kyc?.pan?.value) {
      setPan(customer?.kyc?.pan?.value);
    }
  }, [customer]);

  useEffect(() => {
    if (panVerifyApi?.data) {
      setIsPanChanged(false);
      formikRef.current.setFieldError('pan_number', '');
      const verifiedPan: string = formikRef.current?.values?.pan_number || '';
      if (panVerifyApi.data.data?.name && verifiedPan.length === 10) {
        netcoreTrack(
          'pan_card_details_add',
          {
            customer_id: customerId,
            pan_verification_status: 'VERIFIED',
            pan_masked: `XXXXXX${verifiedPan.slice(-4)}`,
            new_added_at: netcoreDate(new Date()),
          },
          user?.mobile?.value,
          { PAN_VERIFIED: true }
        );
      }
    }
  }, [panVerifyApi.data]);

  useEffect(() => {
    if (updateCustomerApi.data && !updateCustomerApi.error) {
      netcoreTrack(
        'kyc_completed',
        { customer_id: customerId, pan_verification_status: 'VERIFIED' },
        user?.mobile?.value,
        { PAN_VERIFIED: true }
      );
      getCustomerProfileApi.request(customerId,user?.userId);
    } else if (updateCustomerApi.error) {
      toast.error('User with same PAN already exists');
    }
  }, [updateCustomerApi.data, updateCustomerApi.error]);

  useEffect(() => {
    if (
      getCustomerProfileApi.responseCode === 200 &&
      getCustomerProfileApi.data.data
    ) {
      setCustomer(getCustomerProfileApi.data.data.customer);
    } else if (getCustomerProfileApi.error) {
      toast.error(getCustomerProfileApi.error.errors[0].title);
    }
  }, [getCustomerProfileApi.data, getCustomerProfileApi.error]);

  const verifyPan = async () => {
    let panFieldValue = formikRef.current?.values?.pan_number?.toUpperCase();
    if (
      panFieldValue?.length === 10 &&
      /[A-Z]{5}[0-9]{4}[A-Z]{1}/.test(panFieldValue)
    ) {
      let data = {
        pan: panFieldValue,
        isPanVerficationRequired: false,
        user_id: user?.userId,
      };

      panVerifyApi.request(data);
    } else {
      formikRef.current.setFieldError('pan_number', 'Invalid PAN');
    }
  };

  const updateKyc = async () => {
    if (!panVerifyApi?.data?.data?.name) {
      formikRef.current.setFieldError('pan_number', 'Please verify PAN');
      formikRef.current.setFieldTouched('pan_number', true);
      return;
    }

    let patch_req: any = {
      requiredStatus: 'ENABLED',
      kyc: {},
    };
    if (panVerifyApi?.data?.data?.kycId) {
      patch_req.kyc['pan'] = { kycId: panVerifyApi?.data?.data?.kycId };
    }
    updateCustomerApi.request(patch_req, customerId,user?.userId);
  };

  return (
    <>
      {pan ? (
        <>
          <Form.Label htmlFor='disabled_pan'>PAN</Form.Label>
          <InputGroup className='mb-3'>
            <Form.Control
              placeholder='ABCDE1234F'
              aria-label='ABCDE1234F'
              aria-describedby='basic-addon2'
              name='disabled_pan'
              id='disabled_pan'
              value={pan}
              disabled
            />

            <div className='input-group-text px-2'>
              <img src={Verified} width='22px' height='22px' />
            </div>
          </InputGroup>
        </>
      ) : (
        <>
          <Formik
            innerRef={formikRef}
            initialValues={{
              pan_number: '',
              // pan_image: '',
            }}
            onSubmit={() => {
              updateKyc();
            }}
            validationSchema={Yup.object().shape({
              pan_number: Yup.string().required('PAN is required'),
              // pan_image: Yup.mixed().required('PAN image is required'),
            })}
          >
            {({ errors, touched, setFieldTouched, setFieldValue }) => (
              <FormikForm>
                <Form.Group className='mb-2' controlId='tempForm.pan'>
                  <Form.Label>Enter your PAN</Form.Label>
                  <InputGroup className='mb-2'>
                    <Form.Control
                      placeholder='ABCDE1234F'
                      aria-label='ABCDE1234F'
                      aria-describedby='basic-addon2'
                      name='pan_number'
                      onBlur={(e) => {
                        setFieldTouched('pan_number');
                      }}
                      value={panValue}
                      onChange={(e) => {
                        if (e.target.value?.length <= 10) {
                          setPanValue(e.target.value?.toUpperCase());
                          setFieldValue(
                            'pan_number',
                            e.target.value?.toUpperCase()
                          );
                        }
                        setIsPanChanged(true);

                        if (e.target.value?.length === 10) verifyPan();
                      }}
                    />
                    {panVerifyApi.loading ? (
                      <div className='input-group-text px-2'>
                        <Spinner animation='border' size='sm' />
                      </div>
                    ) : (
                      !errors.pan_number &&
                      panVerifyApi?.data?.data?.name &&
                      !isPanChanged && (
                        <div className='input-group-text px-2'>
                          <img src={Verified} width='22px' height='22px' />
                        </div>
                      )
                    )}
                    {isPanChanged && !panVerifyApi.loading && (
                      <button
                        className='btn btn-verify'
                        id='button-pan'
                        type='button'
                        onClick={() => {
                          verifyPan();
                        }}
                      >
                        Verify
                      </button>
                    )}
                  </InputGroup>
                  <div className='error'>
                    {errors.pan_number && touched.pan_number ? (
                      <small id='my-helper-text'>
                        <>
                          <img src={infoIco} alt='' />
                          {errors.pan_number}
                        </>
                      </small>
                    ) : null}
                  </div>
                  {!errors.pan_number &&
                    panVerifyApi?.data?.data?.name &&
                    !isPanChanged && (
                      <div className='d-flex justify-content-between mx-1'>
                        <div className='font-weight-bold'>
                          <b>Name on PAN : {panVerifyApi?.data?.data?.name}</b>
                        </div>
                        {/* <img src={Verified} width='22px' height='22px' /> */}
                      </div>
                    )}
                </Form.Group>

                {/* <div className='form-group mb-3'>
              <label className='form-label'>PAN Image</label>
              <small className='text-muted'> (Optional)</small>
              <div className='input-group mb-3'>
                <Field
                  type='file'
                  placeholder=''
                  name='pan_image'
                  className='form-control'
                  value={values.pan_image}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    fileChanged(e);
                    setFieldValue('pan_image', e.target?.value);
                  }}
                />
                {isPanOcrLoading ? (
                  <div className='input-group-text px-2'>
                    <Spinner animation='border' size='sm' />
                  </div>
                ) : (
                  panOcrKyc?.kycId && (
                    <div className='input-group-text px-2'>
                      <img src={Verified} width='20px' height='20px' />
                    </div>
                  )
                )}
              </div>
              <div className='error'>
                {errors.pan_image && touched.pan_image && (
                  <small id='my-helper-text'>
                    <img src={infoIco} alt='' />
                    {errors.pan_image}
                  </small>
                )}
              </div>
            </div> */}

                {panVerifyApi.responseCode === 200 &&
                panVerifyApi.data &&
                formikRef.current?.values.pan_number?.length === 10 ? (
                  <div className='form-group my-3'>
                    <Button
                      disabled={
                        updateCustomerApi.loading ||
                        getCustomerProfileApi.loading
                      }
                      variant='primary'
                      className='w-100'
                      size='lg'
                      type='submit'
                    >
                      Update{' '}
                      {updateCustomerApi.loading ||
                      getCustomerProfileApi.loading ? (
                        <Spinner animation='border' size='sm' />
                      ) : null}
                    </Button>
                  </div>
                ) : null}
              </FormikForm>
            )}
          </Formik>
        </>
      )}
    </>
  );
};

export default memo(CustomerPan);
